import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { IHoliday, TIME_INTERVAL_TRANSFORMER_SERVICE } from '@luggagehero/shared/interfaces';
import { SharedHolidayService } from '@luggagehero/shared/services/holiday';
import { SharedMomentService } from '@luggagehero/shared/services/moment';
import { TranslateService } from '@ngx-translate/core';

import { BaseComponent } from '../../../core';
import { HolidayOpeningHours } from '../../../services/shops/holiday-opening-hours';
import { OpeningHours } from '../../../services/shops/opening-hours';
import { DateUtil } from '../../../utils/date.util';
import { HolidayUtil } from '../../../utils/holiday.util';

const ONE_DAY_MILLISECONDS = 1000 * 60 * 60 * 24;

@Component({ template: '' })
export abstract class ManageHolidayOpeningHoursBaseComponent extends BaseComponent implements OnInit, AfterViewInit {
  @Input() public openingHours: OpeningHours;
  @Input() public validateHolidays = false;
  @Output() public openingHoursChange: EventEmitter<OpeningHours>;
  @Output() public pristineChange: EventEmitter<boolean>;

  public exceptionDate: Date;
  public minExceptionDate: Date;

  protected momentService = inject(SharedMomentService);
  private timeIntervalService = inject(TIME_INTERVAL_TRANSFORMER_SERVICE);

  private _country: string;
  private _pristine = true;
  private isInitialized = false;

  constructor(
    private holidayService: SharedHolidayService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
  ) {
    super();

    this.openingHoursChange = new EventEmitter<OpeningHours>();
    this.pristineChange = new EventEmitter<boolean>();
  }

  get country(): string {
    return this._country;
  }
  @Input() set country(value: string) {
    this._country = value;
    void this.fetchHolidays();
  }

  get holidays(): HolidayOpeningHours[] {
    if (!this.openingHours || !this.openingHours.holidays) {
      return [];
    }
    return this.openingHours.holidays
      .filter((h) => h.holiday.countryCode === this.country)
      .sort((a, b) => {
        if (!a.holiday.instances || !b.holiday.instances) {
          return 0;
        }
        if (!a.holiday.instances[0]) {
          return 1;
        }
        return a.holiday.instances[0].localeCompare(b.holiday.instances[0]);
      });
  }
  set holidays(value: HolidayOpeningHours[]) {
    this.openingHours.holidays = value;
    this.cd.markForCheck();

    this.openingHoursChange.emit(this.openingHours);
  }

  get valid(): boolean {
    return OpeningHours.isValid(this.openingHours, this.validateHolidays);
  }

  get pristine(): boolean {
    return this._pristine;
  }
  set pristine(value: boolean) {
    this._pristine = value;
    this.cd.markForCheck();

    this.pristineChange.emit(value);
  }

  ngOnInit() {
    const today = DateUtil.setTime(new Date(), 12);
    this.exceptionDate = today;

    const yesterday = new Date(today.getTime() - ONE_DAY_MILLISECONDS);
    this.minExceptionDate = yesterday;

    this.cd.markForCheck();
  }

  ngAfterViewInit() {
    this.isInitialized = true;
  }

  onChange(_e) {
    this.openingHoursChange.emit(this.openingHours);

    if (this.isInitialized) {
      this.pristine = false;
    }
  }

  getHeading(holiday: IHoliday): string {
    if (!holiday) {
      return '';
    }
    return holiday.name;
  }

  getDescription(holiday: IHoliday): string {
    if (!holiday || !holiday.instances || holiday.instances.length === 0) {
      return '';
    }
    const label = this.translate.instant('NEXT_OCCURRENCE') as string;
    const date = this.momentService.formatDate(holiday.instances[0], 'localeDateShort');
    return `${label} ${date}`;
  }

  async fetchHolidays() {
    await this.holidayService.initCountry(this.country);

    const shopHolidayOpeningHours: HolidayOpeningHours[] = [];
    this.holidayService.getHolidays(this.country).forEach((countryHoliday) => {
      // Try to match existing opening hours object stored on the shop
      let openingHours = this.holidays.find((h) => HolidayUtil.isSameHoliday(h.holiday, countryHoliday));

      if (openingHours) {
        // Update holiday instances on existing opening hours object
        openingHours.holiday.instances = countryHoliday.instances;
      } else {
        // Create new opening hours object for this holiday
        openingHours = {
          holiday: countryHoliday,
          openingHours: [{ from: undefined, to: undefined }],
        };
      }
      shopHolidayOpeningHours.push(openingHours);
    });
    this.holidays = shopHolidayOpeningHours;
  }

  setAllClosed() {
    this.holidays.forEach((h) => (h.openingHours = []));
    this.cd.markForCheck();
  }

  setAllOpenAsNormal() {
    this.holidays.forEach((h) => (h.openingHours = null));
    this.cd.markForCheck();
  }

  setAllOpen24Hours() {
    this.holidays.forEach((h) => (h.openingHours = [{ from: 0, to: 1440 }]));
  }
}
